<script setup lang="ts">
import api from "@/app/services/apiDas.ts";
import type { AxiosResponse } from "axios";
import type { ApiResponse } from "@/app/types";
import { onMounted, type PropType, ref, type Ref, watch } from "vue";
import { AdminUnitsSelection } from "@/domains/home/services/nir/forms/components/widgets/types";
import { County, District, Parish, SubCounty, Village } from "@/commons/types/das.ts";

const props = defineProps({
  active: Boolean,
  isUganda: {
    type: Boolean,
    default: true
  },
  modelValue: {
    type: Object as PropType<AdminUnitsSelection>,
  },
  data: {
    type: Object as PropType<AdminUnitsSelection>,
  },
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: AdminUnitsSelection): void
}>()

const districts: Ref<District[] | undefined> = ref()
const counties: Ref<County[] | undefined> = ref()
const subCounties: Ref<SubCounty[] | undefined> = ref()
const parishes: Ref<Parish[] | undefined> = ref()
const villages: Ref<Village[] | undefined> = ref()
const description: Ref<string> = ref("");

const selectedDistrict = ref('')
const selectedCounty = ref('')
const selectedSubCounty = ref('')
const selectedParish = ref('')
const selectedVillage = ref('')

const UGANDA_ID = 'cbafabc8-339c-4a96-b570-9eeb69950649'
let initialData = props.data ? { ...props.data } : null

onMounted(() => {
  fetchDistricts('', 1, 1000)
})

watch(
    () => props.data,
    (data) => {
      if (initialData || !data?.districtId) return
      initialData = { ...data }
      // If districts already loaded (fetch resolved before data arrived), trigger the chain.
      // If not yet loaded, fetchDistricts callback will read initialData when it resolves.
      if (districts.value && districts.value.length > 0) {
        selectedDistrict.value = initialData.districtId ?? ''
        description.value = initialData.description ?? ''
      }
    },
    { immediate: true },
)

const fetchDistricts = async (id: string, page: number, limit: number) => {
  let path = `/admin-units?parentId=${UGANDA_ID}&page=${page}&limit=${limit}`
  if (id != '') path = `/admin-units?parentId=${id}&page=${page}&limit=${limit}`
  return api.get(path).then((response: AxiosResponse<ApiResponse<District[]>>) => {
    districts.value = response.data.data
    selectedDistrict.value = initialData?.districtId ?? ""
    description.value = initialData?.description ?? ""
  })
}

const fetchCounties = async (parentId: string, page: number, limit: number) => {
  return api
      .get(`/admin-units?page=${page}&limit=${limit}&parentId=${parentId}`)
      .then((response: AxiosResponse<ApiResponse<County[]>>) => {
        counties.value = response.data.data
        selectedCounty.value = initialData?.countyId ?? ""
      })
}

const fetchSubcounties = async (parentId: string, page: number, limit: number) => {
  return api
      .get(`/admin-units?page=${page}&limit=${limit}&parentId=${parentId}`)
      .then((response: AxiosResponse<ApiResponse<SubCounty[]>>) => {
        subCounties.value = response.data.data
        selectedSubCounty.value = initialData?.subCountyId ?? ""
      })
}

const fetchParishes = async (parentId: string, page: number, limit: number) => {
  return api
      .get(`/admin-units?page=${page}&limit=${limit}&parentId=${parentId}`)
      .then((response: AxiosResponse<ApiResponse<Parish[]>>) => {
        parishes.value = response.data.data
        selectedParish.value = initialData?.parishId ?? ""
      })
}

const fetchVillages = async (parentId: string, page: number, limit: number) => {
  return api
      .get(`/admin-units?page=${page}&limit=${limit}&parentId=${parentId}`)
      .then((response: AxiosResponse<ApiResponse<Village[]>>) => {
        villages.value = response.data.data
        selectedVillage.value = initialData?.villageId ?? ""
      })
}

// Watch for district change
watch(selectedDistrict, (newValue) => {
  if (newValue) {
    // Reset dependent fields
    selectedCounty.value = ''
    selectedSubCounty.value = ''
    selectedParish.value = ''
    selectedVillage.value = ''
    counties.value = undefined
    subCounties.value = undefined
    parishes.value = undefined
    villages.value = undefined

    // Fetch counties for selected district
    fetchCounties(newValue, 1, 1000)
  }
  emitSelection()
})

// Watch for county change
watch(selectedCounty, (newValue) => {
  if (newValue) {
    // Reset dependent fields
    selectedSubCounty.value = ''
    selectedParish.value = ''
    selectedVillage.value = ''
    subCounties.value = undefined
    parishes.value = undefined
    villages.value = undefined

    // Fetch subcounties for selected county
    fetchSubcounties(newValue, 1, 1000)
  }
  emitSelection()
})

// Watch for subcounty change
watch(selectedSubCounty, (newValue) => {
  if (newValue) {
    // Reset dependent fields
    selectedParish.value = ''
    selectedVillage.value = ''
    parishes.value = undefined
    villages.value = undefined

    // Fetch parishes for selected subcounty
    fetchParishes(newValue, 1, 1000)
  }
  emitSelection()
})

// Watch for parish change
watch(selectedParish, (newValue) => {
  if (newValue) {
    // Reset dependent fields
    selectedVillage.value = ''
    villages.value = undefined

    // Fetch villages for selected parish
    fetchVillages(newValue, 1, 1000)
  }
  emitSelection()
})

// Watch for village change
watch(selectedVillage, () => {
  emitSelection()
})

watch(description, () => {
  emitSelection();
});

function emitSelection() {
  emit('update:modelValue', {
    districtId: selectedDistrict.value,
    countyId: selectedCounty.value,
    subCountyId: selectedSubCounty.value,
    parishId: selectedParish.value,
    villageId: selectedVillage.value,
    description: description.value
  })
}

</script>

<template>
  <div class="flex flex-col p-3 border border-gray-200 rounded-xl">
    <div class="grid grid-cols-1 gap-3">
      <div class="flex flex-col">
        <div v-if="isUganda" class="grid grid-cols-2 gap-2">
          <!-- District -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1.5">
              <i class="fa-solid fa-map-marked-alt text-gray-400 mr-1"></i>
              District\ <i class="text-red-500 text-xs fas fa-asterisk mx-1"></i>
            </label>
            <select
                v-model="selectedDistrict"
                :disabled="!active"
                class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
            >
              <option value="">Select District</option>
              <option v-for="district in districts" :key="district.id" :value="district.id">
                {{ district.name }}
              </option>
            </select>
          </div>

          <!-- County -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1.5">
              <i class="fa-solid fa-map text-gray-400 mr-1"></i>
              County\ <i class="text-red-500 text-xs fas fa-asterisk mx-1"></i>
            </label>
            <select
                v-model="selectedCounty"
                :disabled="!selectedDistrict || !counties || counties.length === 0 && !active"
                class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">
                {{ !selectedDistrict ? 'Select District first' : 'Select County' }}
              </option>
              <option v-for="county in counties" :key="county.id" :value="county.id">
                {{ county.name }}
              </option>
            </select>
          </div>

          <!-- SubCounty -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1.5">
              <i class="fa-solid fa-map-signs text-gray-400 mr-1"></i>
              Sub-County\ <i class="text-red-500 text-xs fas fa-asterisk mx-1"></i>
            </label>
            <select
                v-model="selectedSubCounty"
                :disabled="!selectedCounty || !subCounties || subCounties.length === 0 && !active"
                class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">
                {{ !selectedCounty ? 'Select County first' : 'Select Sub-County' }}
              </option>
              <option v-for="subCounty in subCounties" :key="subCounty.id" :value="subCounty.id">
                {{ subCounty.name }}
              </option>
            </select>
          </div>

          <!-- Parish -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1.5">
              <i class="fa-solid fa-map-pin text-gray-400 mr-1"></i>
              Parish\ <i class="text-red-500 text-xs fas fa-asterisk mx-1"></i>
            </label>
            <select
                v-model="selectedParish"
                :disabled="!selectedSubCounty || !parishes || parishes.length === 0 && !active"
                class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">
                {{ !selectedSubCounty ? 'Select Sub-County first' : 'Select Parish' }}
              </option>
              <option v-for="parish in parishes" :key="parish.id" :value="parish.id">
                {{ parish.name }}
              </option>
            </select>
          </div>

          <!-- Village -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1.5">
              <i class="fa-solid fa-home text-gray-400 mr-1"></i>
              Village\ <i class="text-red-500 text-xs fas fa-asterisk mx-1"></i>
            </label>
            <select
                v-model="selectedVillage"
                :disabled="!selectedParish || !villages || villages.length === 0 && !active"
                class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">
                {{ !selectedParish ? 'Select Parish first' : 'Select Village' }}
              </option>
              <option v-for="village in villages" :key="village.id" :value="village.id">
                {{ village.name }}
              </option>
            </select>
          </div>
        </div>
        <div v-else class="flex flex-col border border-gray-200 rounded-lg p-2">
          <textarea
            class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
            v-model="description"
          ></textarea>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
